import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { fireAudit } from '../utils/auditClient';

const Renewal = () => {
    const today = new Date().toISOString().split('T')[0];
    const { userData } = useUser();
    const userId = userData?.userId;
    const token = localStorage.getItem('auth-token');
    const { selectedCenter } = useUser();
    const [academies, setAcademies] = useState([]);
    const [academyOptions, setAcademyOptions] = useState([]);
    const [sportsOptions, setSportsOptions] = useState([]);
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [playersList, setPlayersList] = useState([]);
    const [generatedOtp, setGeneratedOtp] = useState('');
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [showOtpField, setShowOtpField] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [loadingPayment, setLoadingPayment] = useState(false);
    const [formData, setFormData] = useState({
        date: today,
        academy: null,
        sport: null,
        receivedAmount: '',
        razorpay: '',
        cash: '',
        venueqr: '',
        comments: '',
        name: '',
        contact: '',
        companyName: ''
    });

    useEffect(() => {
        const fetchAcademies = async () => {
            try {
                const response = await axios.get(process.env.REACT_APP_API_URL_ACADEMIES, {
                    headers: {
                        'Content-Type': 'application/json',
                        'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
                    }
                });

                const fetchedAcademies = response.data.data.academies;
                let filteredAcademies = fetchedAcademies;

                if (userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)) {
                    filteredAcademies = fetchedAcademies.filter((academy) =>
                        userData.academyIds.includes(academy.id)
                    );
                }

                setAcademies(filteredAcademies);
                setAcademyOptions(filteredAcademies.map(academy => ({
                    label: academy.name,
                    value: academy.id
                })));
            } catch (error) {
                console.error('Error fetching academies:', error);
            }
        };

        fetchAcademies();
    }, []);

    useEffect(() => {
        if (selectedCenter && academies.length > 0) {
            const selectedAcademy = academies.find((a) => a.id === Number(selectedCenter));

            if (selectedAcademy) {
                const selectedOption = {
                    label: selectedAcademy.name,
                    value: selectedAcademy.id
                };

                setFormData((prev) => ({
                    ...prev,
                    academy: selectedOption,
                    sport: null,
                }));

                if (selectedAcademy.sports) {
                    const sports = selectedAcademy.sports.map((sport) => ({
                        label: sport.name,
                        value: sport.sport_id,
                        image: sport.sport_pic
                    }));
                    setSportsOptions(sports);
                } else {
                    setSportsOptions([]);
                }
            } else {
                console.error('No matching academy found for selectedCenter:', selectedCenter);
            }
        }
    }, [selectedCenter, academies]);

    const handleAcademyChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            academy: selectedOption,
            sport: null
        }));

        const selected = academies.find((a) => a.id === selectedOption.value);
        if (selected && selected.sports) {
            const sports = selected.sports.map((sport) => ({
                label: sport.name,
                value: sport.sport_id,
                image: sport.sport_pic
            }));
            setSportsOptions(sports);
        } else {
            setSportsOptions([]);
        }
    };

    const handlePlayerSelect = (player) => {
        setSelectedPlayer(player);
        setFormData(prev => ({
            ...prev,
            name: player.playerName,
            receivedAmount: player.amount.toString(),
        }));
    };

    const handlePaymentMethodChange = (method) => {
        if (selectedPlayer) {
            setPaymentMethod(method);
            setFormData(prev => ({
                ...prev,
                cash: method === 'cash' ? selectedPlayer.amount.toString() : '',
                razorpay: method === 'razorpay' ? selectedPlayer.amount.toString() : '',
                venueqr: method === 'venueqr' ? selectedPlayer.amount.toString() : '',
            }));
        } else {
            Swal.fire('Error', 'Please select a player before selecting the payment method', 'error');
        }
    };

    const validateForm = () => {
        if (!formData.academy || !formData.academy.value) {
            Swal.fire('Missing Info', 'Please select an academy', 'error');
            return false;
        }
        if (!paymentMethod) {
            Swal.fire('Missing Info', 'Please select a payment method', 'error');
            return false;
        }
        if (!selectedPlayer) {
            Swal.fire('Missing Info', 'Please select a player', 'error');
            return false;
        }
        return true;
    };

    const resetForm = () => {
        setFormData({
            date: today,
            receivedAmount: '',
            razorpay: '',
            cash: '',
            venueqr: '',
            comments: '',
            name: '',
            contact: '',
            companyName: ''
        });
        setMobile('');
        setOtp('');
        setShowOtpField(false);
        setSelectedPlayer(null);
        setPaymentMethod('');
        setPlayersList([]);
        setIsVerified(false);
    };

    const handleCashSubmit = async () => {
        if (!validateForm()) return;
        setLoadingPayment(true);

        const payload = {
            data: {
                dueId: selectedPlayer.dueid,
                academyId: formData.academy.value,
                paidAmount: parseFloat(formData.receivedAmount),
                dueAmount: parseFloat(selectedPlayer.amount),
                isSettled: true,
                paymentMethod: 'CASH',
                remark: "Paid at center",
                givenBy: userId,
            }
        };
 
        try {
            await axios.post(process.env.REACT_APP_API_URL_RENEWAL, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            await fireAudit({
                entity: 'renewal_payment',
                affected_id: String(selectedPlayer.dueid),
                action: 'create',
                section: 'Coaching',
                subSection: 'Renewal',
                businessApi: process.env.REACT_APP_API_URL_RENEWAL,
                businessMethod: 'POST',
                oldPayload: {},
                newPayload: payload,
                extra: { academyId: formData.academy.value, method: 'CASH' }
            });

            Swal.fire({
                icon: 'success',
                title: 'Payment Successful',
                toast: true,
                text: 'The payment has been processed successfully.',
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
            resetForm();
        } catch (error) {
            const message = error?.response?.data?.error_message || 'Failed to process payment';
            console.error('API Error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Payment Failed',
                text: message,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
        } finally {
            setLoadingPayment(false);
        }
    };

    const handleRazorpaySubmit = async () => {
        if (!validateForm()) return;

        try {
            const rawInput = formData.receivedAmount;

            if (!rawInput || isNaN(rawInput)) {
                Swal.fire('Invalid Amount', 'Received amount is missing or invalid.', 'error');
                return;
            }

            const receivedAmount = parseFloat(rawInput);
            const amountInPaise = Math.round(receivedAmount * 100);

            const options = {
                key: process.env.REACT_APP_API_RAZORPAY_KEY,
                amount: amountInPaise,
                currency: 'INR',
                name: 'Machaxi',
                description: `Walkin: Payment for Academy - ${formData.academy?.label || 'Unknown'}`,
                handler: async function (response) {
                    setLoadingPayment(true);

                    try {
                        const payload = {
                            data: {
                                dueId: selectedPlayer.dueid,
                                academyId: formData.academy.value,
                                paidAmount: receivedAmount,
                                dueAmount: parseFloat(selectedPlayer.amount),
                                isSettled: true,
                                paymentMethod: 'RAZORPAY',
                                remark: 'Paid at center',
                                givenBy: userId,
                                payment_details: {
                                    razorpay_payment_id: response.razorpay_payment_id,
                                },
                            },
                        };

                        const res = await axios.post(process.env.REACT_APP_API_RENEWAL_RAZORPAY, payload, {
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                        });
    
                        const responseData = res.data;

                        if (responseData.success) {
                            await fireAudit({
                                entity: 'renewal_payment',
                                affected_id: String(selectedPlayer.dueid),
                                action: 'create',
                                section: 'Coaching',
                                subSection: 'Renewal',
                                businessApi: process.env.REACT_APP_API_RENEWAL_RAZORPAY,
                                businessMethod: 'POST',
                                oldPayload: {},
                                newPayload: payload,
                                extra: { academyId: formData.academy.value, method: 'RAZORPAY', paymentId: response.razorpay_payment_id }
                            });

                            Swal.fire({
                                icon: 'success',
                                title: 'Payment Successful',
                                text: responseData.success_message || 'Razorpay payment completed successfully.',
                                toast: true,
                                position: 'top-end',
                                showConfirmButton: false,
                                timer: 3000,
                            });
                            resetForm();
                        } else {
                            const detailedError = responseData.data?.Error || responseData.error_message || 'Something went wrong.';
                            Swal.fire({
                                icon: 'error',
                                title: 'Payment Failed',
                                text: detailedError,
                                toast: true,
                                position: 'top-end',
                                showConfirmButton: false,
                                timer: 3000,
                            });
                        }

                    } catch (err) {
                        const message = err?.response?.data?.error_message || 'Failed to process payment';
                        console.error('API Error:', err);
                        Swal.fire({
                            icon: 'error',
                            title: 'Payment Failed',
                            text: message,
                            toast: true,
                            position: 'top-end',
                            showConfirmButton: false,
                            timer: 3000,
                        });
                    } finally {
                        setLoadingPayment(false); 
                    }
                },
                prefill: {
                    name: formData.name,
                    contact: mobile,
                },
                theme: {
                    color: '#3399cc',
                },
                modal: {
                    ondismiss: () => {
                        Swal.fire({
                            icon: 'error',
                            title: 'Payment Cancelled',
                            text: 'Razorpay payment was cancelled by the user.',
                            toast: true,
                            position: 'top-end',
                            showConfirmButton: false,
                            timer: 3000,
                        });
                        setLoadingPayment(false); 
                    },
                },
            };

            setLoadingPayment(true); 
            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            console.error('Razorpay Init Error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Failed to initiate Razorpay',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
            setLoadingPayment(false); 
        }
    };

    const handleSendOtp = async () => {
        if (loading) return;
        if (mobile.length !== 10) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Mobile Number',
                text: 'Please enter a valid 10-digit mobile number',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
            return;
        }
        setLoading(true);
        const formattedContactNumber = mobile.startsWith('+91') ? mobile : `+91${mobile}`;
    
        try {
            const otpResponse = await axios.post(process.env.REACT_APP_API_URL_OTP_SEND, {
                data: {
                    phone_number: formattedContactNumber,
                    has_firebase_check: false,
                },
            });
    
            if (otpResponse.data.success) {
                setShowOtpField(true);
                setIsVerified(false);
                setOtp('');
    
                Swal.fire({
                    icon: 'success',
                    title: 'OTP Sent',
                    text: `OTP sent to ${formattedContactNumber}`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                });
    
                await fetchUserData(formattedContactNumber);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to Send OTP',
                    text: otpResponse.data.message || 'Unable to send OTP. Please try again.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to send OTP. Please try again.',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
        } finally {
            setLoading(false);
        }
    };
    
    const fetchUserData = async (formattedContactNumber) => {
        const apiKey = process.env.REACT_APP_API_KEY;
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
        try {
            const response = await axios.post(
                process.env.REACT_APP_API_URL_FETCH_USER,
                { mobileNumber: formattedContactNumber },
                {
                    headers: {
                        'x-api-key': apiKey,
                    },
                }
            );

            const { parent, children } = response.data.userDetails;

            const parentId = parent ? parent.id : null;
            const childrenIds = children ? children.map(child => child.id) : [];

            if (!parentId && childrenIds.length === 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'No User Found',
                    text: 'No parent or children data found for this mobile number.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                });
                return;
            }

            const allUserIds = parentId ? [parentId, ...childrenIds] : childrenIds;
            const playersResponse = await axios.get(
                `${process.env.REACT_APP_API_URL_GET_PLAYER_DETAILS}?userIds=${allUserIds.join(',')}`,
                { headers }
            );

            const playerIds = playersResponse.data.data.players;
            if (playerIds.length === 0) {
                Swal.fire({
                    icon: 'info',
                    title: 'No Players Found',
                    text: 'No players associated with this user',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                });
                return;
            }

            const duesResponse = await axios.get(
                `${process.env.REACT_APP_API_URL_DETAILS_PAYMENT_DUES}?playerIds=${playerIds.join(',')}`,
                { headers }
            );

            const duesData = duesResponse.data.data.dues;
            if (duesData.length === 0) {
                Swal.fire({
                    icon: 'info',
                    title: 'No Dues Found',
                    text: 'No players have dues',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                });
                return;
            }

            const duesDetails = duesData.map(due => ({
                playerName: due.playerName,
                sportName: due.sportName,
                amount: due.amount,
                dueid: due.dueId,
            }));

            setPlayersList(duesDetails);
            setSelectedPlayer(duesDetails[0] || null);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to process user and player data',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
        }
    };
    
    const handleVerifyOtp = async () => {
        try {
            const formattedContactNumber = mobile.startsWith('+91') ? mobile : `+91${mobile}`;
            const response = await axios.post(process.env.REACT_APP_API_URL_OTP_VERIFY, {
                data: {
                    phone_number: formattedContactNumber,
                    has_firebase_check: false,
                    otp: otp,
                },
            });
    
            if (response.data.success) {
                setIsVerified(true);
    
                Swal.fire({
                    icon: 'success',
                    title: 'OTP Verified',
                    text: 'OTP has been verified successfully.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid OTP',
                    text: response.data.message || 'Invalid OTP. Please try again.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to verify OTP. Please try again.',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000,
            });
        }
    };
    

    const handleMobileChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
        setMobile(value);
        setFormData(prev => ({ ...prev, contact: value }));
        setShowOtpField(false);
        setSelectedPlayer(null);
        setIsVerified(false);
        setGeneratedOtp('');
    };

    const handleVenueQRSubmit = async () => {
        if (!validateForm()) return;
        setLoadingPayment(true);

        const payload = {
            data: {
                dueId: selectedPlayer.dueid,
                academyId: formData.academy.value,
                paidAmount: parseFloat(formData.receivedAmount),
                dueAmount: parseFloat(selectedPlayer.amount),
                isSettled: true,
                paymentMethod: 'UPI',
                remark: "Paid at center",
                givenBy: userId,
            }
        };

        try {
            await axios.post(process.env.REACT_APP_API_URL_RENEWAL, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            await fireAudit({
                entity: 'renewal_payment',
                affected_id: String(selectedPlayer.dueid),
                action: 'create',
                section: 'Payments',
                subSection: 'Renewal',
                businessApi: process.env.REACT_APP_API_URL_RENEWAL,
                businessMethod: 'POST',
                oldPayload: {},
                newPayload: payload,
                extra: { academyId: formData.academy.value, method: 'UPI' }
            });

            Swal.fire({
                icon: 'success',
                title: 'Payment Successful',
                toast: true,
                text: 'The payment has been processed successfully.',
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
            resetForm();
        } catch (error) {
            const message = error?.response?.data?.error_message || 'Failed to process payment';
            console.error('API Error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Payment Failed',
                text: message,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
        } finally {
            setLoadingPayment(false);
        }
    };

    return (
        <div style={{ margin: 'auto' }}>

            <form onSubmit={(e) => {
                e.preventDefault();
                if (paymentMethod === 'cash') handleCashSubmit();
                else if (paymentMethod === 'razorpay') handleRazorpaySubmit();
                else if (paymentMethod === 'venueqr') handleVenueQRSubmit();
            }}>

                <div>
                    <div className="floating-select-form" style={{ width: '100%' }}>
                        <Select
                            name="academy"
                            options={academyOptions}
                            value={formData.academy}
                            onChange={handleAcademyChange}
                            placeholder="Select Academy"
                            className={`walkin-form-dropdown ${formData.academy ? 'has-value' : ''}`}
                            classNamePrefix="walkin-select"
                            isClearable
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    padding: '5px',
                                }),
                                placeholder: (provided) => ({
                                    ...provided,
                                    color: 'black',
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }), 
                            }}
                            menuPortalTarget={document.body} 
            
                        />
                        {formData.academy && <label htmlFor="academy">Academy</label>}
                    </div>
                </div>

                <div style={{ marginBottom: '25px' }}>
                    <div className="floating-select-form" style={{ width: '98%' }}>
                        <input
                            type="tel"
                            name="mobile"
                            value={mobile}
                            onChange={handleMobileChange}
                            className={`walkin-form ${mobile.length === 10 ? 'has-value' : ''}`}
                            placeholder="Enter 10-digit mobile number"
                            maxLength="10"
                        />
                        {mobile.length === 10 && <label htmlFor="mobile">Mobile Number</label>}
                    </div>
                    {loadingPayment && (
                        <div className="loading-overlay">
                            <div className="loading-spinner">
                                <span>Processing Payment...</span>
                                <div className="spinner"></div>
                            </div>
                        </div>
                    )}

                    <div>
                        {mobile.length === 10 && !isVerified && !showOtpField && (
                            <button
                                type="button"
                                onClick={handleSendOtp}
                                style={{
                                    padding: '8px 16px',
                                    background: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                                disabled={loading}
                            >
                                {loading ? 'Sending OTP...' : 'Send OTP'}
                            </button>
                        )}

                    </div>

                    {showOtpField && !isVerified && (
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Enter OTP</label>
                            <input
                                type="tel"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                maxLength="6"
                                placeholder="Enter 6-digit OTP"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    marginBottom: '10px'
                                }}
                            />
                            <button
                                type="button"
                                onClick={handleVerifyOtp}
                                disabled={otp.length !== 6}
                                style={{
                                    padding: '8px 16px',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Verify OTP
                            </button>
                        </div>
                    )}

                    {isVerified && (
                        <div className="category-selection-section" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Select Player</label>
                            <div className="sport-cards-container">
                                {Array.isArray(playersList) && playersList.map(player => (
                                    <div
                                        key={player.dueid}
                                        className={`sport-card ${selectedPlayer && selectedPlayer.dueid === player.dueid ? 'selected' : ''}`}
                                        onClick={() => handlePlayerSelect(player)}
                                    >
                                        {player.playerName} ({player.sportName}) - ₹{player.amount}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '20px' }}>
                        <label>
                            <input
                                type="radio"
                                name="paymentMethod"
                                value="cash"
                                checked={paymentMethod === 'cash'}
                                onChange={() => handlePaymentMethodChange('cash')}
                            />
                            Cash
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="paymentMethod"
                                value="razorpay"
                                checked={paymentMethod === 'razorpay'}
                                onChange={() => handlePaymentMethodChange('razorpay')}
                            />
                            Razor Pay
                        </label>
                        {hasPrivilege(userData, 'PAYMENT', 'VENUE_QR') && (
    <label>
      <input
        type="radio"
        name="paymentMethod"
        value="venueqr"
        checked={paymentMethod === 'venueqr'}
        onChange={() => handlePaymentMethodChange('venueqr')}
      />
      Venue QR
    </label>
  )}
                    </div>

                </div>

                <button
                    type="submit"
                    style={{
                        width: '100%',
                        padding: '12px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '16px',
                        cursor: 'pointer'
                    }}
                    disabled={loadingPayment}
                >

                    {loadingPayment ? 'Processing Payment...' : 'Complete Payment'}
                </button>
            </form>
        </div>
    );
};

export default Renewal;
